/**
 * Orden por columna para las tablas jerárquicas de Vivienda Gerencia
 * (Proyecto → Bloque → Obra, Tarea → Partida…). El orden se aplica entre
 * HERMANOS de cada nivel, así la jerarquía se conserva: se reordenan los
 * proyectos entre sí, los bloques dentro de su proyecto y las obras dentro de
 * su bloque.
 */
export interface OrdenColumna {
  /** Índice de la columna (posición en el arreglo de valores de la fila). */
  idx: number;
  dir: 'desc' | 'asc';
}

/** Ciclo al hacer click en un encabezado: mayor a menor → menor a mayor → orden original. */
export function siguienteOrden(actual: OrdenColumna | null, idx: number): OrdenColumna | null {
  if (!actual || actual.idx !== idx) return { idx, dir: 'desc' };
  return actual.dir === 'desc' ? { idx, dir: 'asc' } : null;
}

/**
 * Devuelve los hermanos ordenados por la columna elegida; sin orden devuelve
 * el arreglo tal cual (orden natural). Los valores vacíos (null/NaN, mostrados
 * como "—") van siempre al final, sea cual sea la dirección; los empates
 * conservan el orden original.
 */
export function ordenarHermanos<T>(
  items: T[],
  orden: OrdenColumna | null,
  valores: (item: T) => (number | null)[],
): T[] {
  if (!orden) return items;
  const signo = orden.dir === 'desc' ? -1 : 1;
  const clave = (item: T): number | null => {
    const v = valores(item)[orden.idx];
    return v === null || v === undefined || Number.isNaN(v) ? null : v;
  };
  return [...items].sort((a, b) => {
    const va = clave(a);
    const vb = clave(b);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    return signo * (va - vb);
  });
}
